import { FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolvePanelHomePath } from '@/application/navigation/menuHouses';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { LanguageSelector } from '@/presentation/components/common/LanguageSelector';
import { ThemeToggle } from '@/presentation/components/common/ThemeToggle';
import { DevImpersonation } from '@/presentation/components/common/DevImpersonation';
import { LoadingSpinner } from '@/presentation/components/common/LoadingSpinner';

export function LoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const { hasPermission, isOwner } = usePermissions();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [requiresTwoFactorSetup, setRequiresTwoFactorSetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null);
  const [savedCredentials, setSavedCredentials] = useState<{ username: string; password: string } | null>(null);

  if (user) {
    return <Navigate to={resolvePanelHomePath(hasPermission, isOwner)} replace />;
  }

  function mapLoginError(err: unknown): string {
    const code = err instanceof Error ? err.message : '';
    if (code === 'network_error') return t('auth.networkError');
    if (code === 'login_failed') return t('auth.loginFailed');
    return t('auth.invalidCredentials');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login({ username, password });
      if (result.requiresTwoFactorSetup) {
        setRequiresTwoFactorSetup(true);
        setQrCodeUrl(result.twoFactorQrCodeUrl ?? null);
        setTwoFactorSecret(result.twoFactorSecret ?? null);
        setSavedCredentials({ username, password });
        setTwoFactorCode('');
        return;
      }
      if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setSavedCredentials({ username, password });
        setTwoFactorCode('');
      }
    } catch (err) {
      setError(mapLoginError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleTwoFactorSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!savedCredentials) return;
    const cleanCode = twoFactorCode.trim().replace(/\s/g, '');
    if (!/^\d{6}$/.test(cleanCode)) {
      setError(t('auth.twoFactorRequired'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      await login({
        username: savedCredentials.username,
        password: savedCredentials.password,
        twoFactorCode: cleanCode,
        twoFactorSecret: requiresTwoFactorSetup ? twoFactorSecret ?? undefined : undefined,
      });
    } catch (err) {
      setError(mapLoginError(err));
    } finally {
      setLoading(false);
    }
  }

  const chrome = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <img src="/assets/images/coofit-logo.png" alt="Coofit" className="h-9 w-auto object-contain" />
        <h1 className="panel-page-title mt-3">
          {requiresTwoFactorSetup ? t('auth.setup2FA') : requiresTwoFactor ? t('auth.twoFactorAuth') : t('auth.login')}
        </h1>
        <p className="panel-page-subtitle">
          {requiresTwoFactorSetup
            ? t('auth.scanQRCode')
            : requiresTwoFactor
              ? t('auth.enter2FACode')
              : t('auth.loginSubtitle')}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>
    </div>
  );

  if (requiresTwoFactorSetup && qrCodeUrl) {
    return (
      <div className="panel-auth-page">
        <div className="panel-auth-card">
          {chrome}
          <div className="mt-6 flex justify-center">
            <img src={qrCodeUrl} alt={t('auth.setup2FA')} className="h-56 w-56 rounded-lg bg-white p-2" />
          </div>
          <form onSubmit={handleTwoFactorSubmit} className="mt-6 space-y-5">
            <label className="block">
              <span className="panel-auth-label">{t('auth.twoFactorCode')}</span>
              <input
                value={twoFactorCode}
                onChange={(event) => {
                  setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                  setError('');
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                maxLength={6}
                className="panel-auth-input"
                autoFocus
                required
              />
            </label>
            {error && <div className="panel-auth-error">{error}</div>}
            <button type="submit" disabled={loading} className="panel-auth-submit">
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="border-white/30 border-t-white" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.verify')
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (requiresTwoFactor) {
    return (
      <div className="panel-auth-page">
        <div className="panel-auth-card">
          {chrome}
          <form onSubmit={handleTwoFactorSubmit} className="mt-8 space-y-5">
            <label className="block">
              <span className="panel-auth-label">{t('auth.twoFactorCode')}</span>
              <input
                value={twoFactorCode}
                onChange={(event) => {
                  setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                  setError('');
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                maxLength={6}
                className="panel-auth-input"
                autoFocus
                required
              />
            </label>
            {error && <div className="panel-auth-error">{error}</div>}
            <button type="submit" disabled={loading} className="panel-auth-submit">
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="border-white/30 border-t-white" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.verify')
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-auth-page">
      <div className="panel-auth-card">
        {chrome}

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <label className="block">
            <span className="panel-auth-label">{t('auth.username')}</span>
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              type="text"
              inputMode="email"
              autoComplete="username"
              placeholder={t('auth.usernamePlaceholder')}
              className="panel-auth-input"
              autoFocus
              required
            />
          </label>

          <div className="block">
            <label htmlFor="login-password" className="panel-auth-label">
              {t('auth.password')}
            </label>
            <div className="relative mt-2">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                className="panel-auth-input pr-11"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center transition-colors"
                style={{ color: 'var(--color-text-subtle)' }}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42L21 21M12 12l.01.01"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="panel-auth-error">{error}</div>}

          <button type="submit" disabled={loading} className="panel-auth-submit">
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="border-white/30 border-t-white" />
                {t('common.loading')}
              </>
            ) : (
              t('auth.login')
            )}
          </button>
        </form>

        <DevImpersonation />
      </div>
    </div>
  );
}
